<template>
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center">
        <h5 class="mb-0">{{ t('profile.sections.ports') }}</h5>
        <span v-if="ports.length" class="text-muted ms-2" style="font-size: 0.9rem; line-height: 1.2;">
          ({{ ports.length }} / {{ totalItems }})
        </span>
      </div>
      <div>
        <a href="#" class="btn btn-sm btn-primary" @click.prevent="openPortModal" v-if="canAssignPortToTenant">
          <i class="ti ti-plus me-1"></i>
          {{ t('common.actions.add') }}
        </a>
      </div>
    </div>
    <div class="card-body position-relative">
      <!-- Loading state -->
      <div v-if="isLoading && !loadingMore" class="text-center py-3">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      
      <!-- Error message -->
      <div v-if="error" class="alert alert-danger" role="alert">
        {{ error }}
      </div>
      
      <!-- Empty state -->
      <div v-if="!isLoading && !ports.length" class="text-center py-3">
        <p class="text-muted">{{ t('profile.ports.noPorts') }}</p>
      </div>
      
      <!-- Port list -->
      <div v-for="port in ports" :key="port.id" class="d-flex align-items-center justify-content-between w-100 mb-1 port-item">
        <div class="d-flex align-items-center">
          <i class="ti ti-map-pin text-primary me-2"></i>
          <div>
            <p class="mb-0 fw-medium">{{ port.portName || port.name }}</p>
            <p class="mb-0 small text-muted">
              <span v-if="port.portCode || port.code">{{ port.portCode || port.code }}</span>
              <span v-if="(port.portCode || port.code) && port.countryName"> • </span>
              <span v-if="port.countryName">{{ port.countryName }}</span>
            </p>
          </div>
        </div>
        <a href="#" 
           class="text-danger"
           @click.prevent="deletePort(port)"
           :class="{ 'disabled': deletingId === port.id }"
           v-if="canDeletePorts">
          <i v-if="deletingId === port.id" class="ti ti-loader ti-spin"></i>
          <i v-else class="ti ti-trash"></i>
        </a>
      </div>
      
      <!-- Load more section -->
      <div v-if="hasMorePages" class="text-center py-3 mt-3">
        <button 
          type="button" 
          class="btn btn-outline-primary" 
          @click="loadMorePorts" 
          :disabled="loadingMore">
          <i v-if="loadingMore" class="ti ti-loader ti-spin me-1"></i>
          <span v-if="!loadingMore">{{ t('common.showMore') || 'Show More' }}</span>
          <span v-else>{{ t('common.loading') || 'Loading...' }}</span>
        </button>
      </div>
    </div>
    
    <!-- Port Selection Modal -->
    <div class="modal fade" id="portSelectionModal" tabindex="-1" aria-labelledby="portSelectionModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="portSelectionModalLabel">{{ t('profile.ports.selectPorts') }}</h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-0">
            <!-- Search input -->
            <div class="p-3 border-bottom">
              <div class="input-group">
                <span class="input-group-text"><i class="ti ti-search"></i></span>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="searchQuery" 
                  :placeholder="t('profile.ports.searchCountryOrPort') || 'Search country or port...'"
                  @input="filterCountries">
              </div>
            </div>
            
            <!-- Loading state for countries -->
            <div v-if="isLoadingCountries" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            
            <!-- Countries Accordion -->
            <div v-if="!isLoadingCountries" class="port-selection-list">
              <div class="accordion" id="portCountryAccordion">
                <div 
                  v-for="country in filteredCountryOptions" 
                  :key="country.id" 
                  class="accordion-item">
                  <h2 class="accordion-header">
                    <button 
                      class="accordion-button collapsed"
                      type="button"
                      @click="toggleCountryAccordion(country.id)"
                      :aria-expanded="expandedCountries[country.id] ? 'true' : 'false'"
                      :aria-controls="`collapse-${country.id}`">
                      <div class="d-flex align-items-center w-100">
                        <strong>{{ country.name }}</strong>
                      </div>
                    </button>
                  </h2>
                  <div 
                    :id="`collapse-${country.id}`"
                    class="accordion-collapse collapse"
                    :class="{ 'show': expandedCountries[country.id] }">
                    <div class="accordion-body p-0">
                      <!-- Loading ports for this country -->
                      <div v-if="loadingCountryPorts[country.id]" class="text-center py-3">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                          <span class="visually-hidden">Loading ports...</span>
                        </div>
                      </div>
                      
                      <!-- Ports list -->
                      <div v-else-if="countryPorts[country.id] && countryPorts[country.id].length > 0">
                        <div 
                          v-for="port in getFilteredPortsForCountry(country.id)" 
                          :key="port.id" 
                          class="port-selection-item"
                          :class="{'selected': selectedPortIds.includes(String(port.id))}">
                          <label class="port-selection-label w-100">
                            <div class="d-flex align-items-center">
                              <input 
                                type="checkbox" 
                                class="form-check-input me-3" 
                                :id="`port-${port.id}`"
                                v-model="selectedPortIds"
                                :value="String(port.id)">
                              <div>
                                <span>{{ port.portName || port.name }}</span>
                                <span v-if="port.portCode || port.code" class="ms-1 small text-muted">({{ port.portCode || port.code }})</span>
                              </div>
                            </div>
                          </label>
                        </div>
                        <!-- No results after search -->
                        <div v-if="getFilteredPortsForCountry(country.id).length === 0" class="text-center py-3">
                          <p class="text-muted small">{{ t('common.noResults') }}</p>
                        </div>
                      </div>
                      
                      <!-- No ports -->
                      <div v-else class="text-center py-3">
                        <p class="text-muted small">{{ t('profile.ports.noPorts') }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- No countries found -->
              <div v-if="filteredCountryOptions.length === 0" class="text-center py-5">
                <p class="text-muted">{{ t('common.noResults') }}</p>
              </div>
            </div>
          </div>
          <div class="modal-footer d-flex justify-content-between">
            <div class="text-muted small">
              {{ selectedPortIds.length }} {{ t('profile.ports.portsSelected') }}
            </div>
            <div>
              <button type="button" class="btn btn-secondary" @click="closeModal">
                {{ t('common.cancel') }}
              </button>
              <button 
                type="button" 
                class="btn btn-primary ms-2" 
                :disabled="!selectedPortIds.length || isSaving"
                @click="savePorts">
                <i v-if="isSaving" class="ti ti-loader ti-spin me-1"></i>
                {{ t('common.save') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import portService from '@/services/portService';
import tenantInfoService from '@/services/tenantInfoService';
import { useI18n } from 'vue-i18n';

export default {
  name: 'ProfilePortCard',
  emits: ['ports-updated'],
  props: {
    canAssignPortToTenant: {
      type: Boolean,
      default: false
    },
    canEditPorts: {
      type: Boolean,
      default: false
    },
    canDeletePorts: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const ports = ref([]);
    const isLoading = ref(false);
    const error = ref(null);
    const currentUser = computed(() => store.getters['auth/currentUser']);
    const deletingId = ref(null);
    
    // Pagination
    const currentPage = ref(0);
    const pageSize = ref(10);
    const totalItems = ref(0);
    const loadingMore = ref(false);
    
    // Computed property to determine if there are more pages to load
    const hasMorePages = computed(() => {
      return ports.value.length < totalItems.value;
    });
    
    // Port selection modal
    const selectedPortIds = ref([]);
    const searchQuery = ref('');
    const countryOptions = ref([]);
    const filteredCountryOptions = ref([]);
    const countryPorts = ref({}); 
    const loadingCountryPorts = ref({});
    const countryPortsLoaded = ref({}); 
    const expandedCountries = ref({}); 
    const isLoadingCountries = ref(false);
    const isSaving = ref(false);
    let portSelectionModal = null;
    
    // Methods to manage ports by country
    const getPortsByCountry = (countryId) => {
      return countryPorts.value[countryId] || [];
    };
    
    const getFilteredPortsForCountry = (countryId) => {
      const ports = getPortsByCountry(countryId);
      if (!searchQuery.value.trim()) {
        return ports;
      }
      
      const query = searchQuery.value.toLowerCase().trim();
      return ports.filter(port => 
        (port.portName || port.name || '').toLowerCase().includes(query) || 
        (port.portCode || port.code || '').toLowerCase().includes(query)
      );
    };
    
    const getSelectedPortCountForCountry = (countryId) => {
      const ports = getPortsByCountry(countryId);
      return ports.filter(port => 
        selectedPortIds.value.includes(String(port.id))
      ).length;
    };
    
    const getPreSelectedCountForCountry = (countryName) => {
      // Count how many ports are already selected for this country
      return ports.value.filter(port => 
        port.countryName === countryName
      ).length;
    };
    
    let searchTimeout = null;
    const filterCountries = async () => {
      // Clear previous timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      
      // Debounce search to avoid too many API calls
      searchTimeout = setTimeout(async () => {
        isLoadingCountries.value = true;
        try {
          const response = await portService.getCountrySelectionItems(searchQuery.value.trim());
          if (response) {
            countryOptions.value = response;
            // Sort countries: ones with selected ports first
            const sorted = [...response].sort((a, b) => {
              const aHasSelected = getPreSelectedCountForCountry(a.name) > 0;
              const bHasSelected = getPreSelectedCountForCountry(b.name) > 0;
              
              if (aHasSelected && !bHasSelected) return -1;
              if (!aHasSelected && bHasSelected) return 1;
              return 0; // Keep original order for same selection status
            });
            filteredCountryOptions.value = sorted;
          }
        } catch (err) {
          console.error('Error searching countries:', err);
        } finally {
          isLoadingCountries.value = false;
        }
      }, 500); // 500ms debounce
    };
    
    const loadPorts = async () => {
      if (!currentUser.value?.tenantId) {
        console.warn('No tenantId found in user data');
        return;
      }
      
      isLoading.value = true;
      error.value = null;
      currentPage.value = 0;
      
      try {
        const response = await portService.searchPorts({
          page: currentPage.value,
          pageSize: pageSize.value,
          tenantId: currentUser.value.tenantId
        });
        
        if (response) {
          ports.value = response.items || [];
          totalItems.value = response.rowCount || response.totalCount || 0;
        }
      } catch (err) {
        console.error('Error loading ports:', err);
        error.value = t('profile.ports.loadError', 'Failed to load ports');
        store.dispatch('notify', {
          message: error.value,
          type: 'error'
        });
      } finally {
        isLoading.value = false;
      }
    };
    
    const loadMorePorts = async () => {
      if (!currentUser.value?.tenantId || !hasMorePages.value) {
        return;
      }
      
      loadingMore.value = true;
      currentPage.value += 1;
      
      try {
        const response = await portService.searchPorts({
          page: currentPage.value,
          pageSize: pageSize.value,
          tenantId: currentUser.value.tenantId
        });
        
        if (response && response.items) {
          // Filter out duplicates based on id
          const newPorts = response.items.filter(
            newPort => !ports.value.some(existingPort => existingPort.id === newPort.id)
          );
          
          ports.value = [...ports.value, ...newPorts];
          totalItems.value = response.rowCount || response.totalCount || 0;
        }
      } catch (err) {
        console.error('Error loading more ports:', err);
        store.dispatch('notify', {
          message: t('profile.ports.loadMoreError', 'Failed to load more ports'),
          type: 'error'
        });
        // Revert page increment on error
        currentPage.value -= 1;
      } finally {
        loadingMore.value = false;
      }
    };
    
    // Update entity counts in store and emit event
    const updatePortsCount = async () => {
      try {
        // Get updated counts from API
        const response = await tenantInfoService.getEntityCounts();
        if (response) {
          // Update store with new counts 
          store.commit('updateEntityCounts', response);
          
          // Emit event to notify parent components
          emit('ports-updated', {
            count: ports.value.length,
            totalSize: totalItems.value
          });
        }
      } catch (error) {
        console.error('Error updating port counts:', error);
      }
    };

    const deletePort = async (port) => {
      if (!port || !port.id) {
        console.error('Invalid port object');
        return;
      }
      
      try {
        deletingId.value = port.id;
        
        await portService.deletePort(port.id);
        
        // Remove port from list
        ports.value = ports.value.filter(p => p.id !== port.id);
        
        // Update entity counts
        await updatePortsCount();
        
        store.dispatch('notify', {
          message: t('profile.ports.deleteSuccess', 'Port deleted successfully'),
          type: 'success'
        });
      } catch (err) {
        console.error('Error deleting port:', err);
        store.dispatch('notify', {
          message: t('profile.ports.deleteError', 'Failed to delete port'),
          type: 'error'
        });
      } finally {
        deletingId.value = null;
      }
    };
    
    const loadCountryOptions = async (searchText = '') => {
      isLoadingCountries.value = true;
      try {
        const response = await portService.getCountrySelectionItems(searchText);
        if (response) {
          countryOptions.value = response;
          // Sort countries: ones with selected ports first
          const sorted = [...response].sort((a, b) => {
            const aHasSelected = getPreSelectedCountForCountry(a.name) > 0;
            const bHasSelected = getPreSelectedCountForCountry(b.name) > 0;
            
            if (aHasSelected && !bHasSelected) return -1;
            if (!aHasSelected && bHasSelected) return 1;
            return 0; // Keep original order for same selection status
          });
          filteredCountryOptions.value = sorted;
        }
      } catch (err) {
        console.error('Error loading country options:', err);
        store.dispatch('notify', {
          message: t('profile.ports.loadError', 'Failed to load countries'),
          type: 'error'
        });
      } finally {
        isLoadingCountries.value = false;
      }
    };
    
    const loadPortsForCountry = async (countryId) => {
      // Check if already loaded or loading
      if (countryPortsLoaded.value[countryId] || loadingCountryPorts.value[countryId]) {
        return;
      }
      
      loadingCountryPorts.value[countryId] = true;
      
      try {
        const response = await portService.getPortSelectionItems(countryId);
        if (response) {
          countryPorts.value[countryId] = response;
          countryPortsLoaded.value[countryId] = true;
          
          // Pre-select ports that are already added (only on first load)
          if (!selectedPortIds.value.length) {
            const currentPortIds = ports.value.map(port => {
              return String(port.portID || port.id || port.portId);
            }).filter(Boolean);
            selectedPortIds.value = [...currentPortIds];
          }
        }
      } catch (err) {
        console.error('Error loading ports for country:', err);
        store.dispatch('notify', {
          message: t('profile.ports.loadError', 'Failed to load ports'),
          type: 'error'
        });
      } finally {
        loadingCountryPorts.value[countryId] = false;
      }
    };
    
    const toggleCountryAccordion = async (countryId) => {
      // Toggle expanded state
      expandedCountries.value[countryId] = !expandedCountries.value[countryId];
      
      // Load ports if expanding and not already loaded
      if (expandedCountries.value[countryId] && !countryPortsLoaded.value[countryId]) {
        await loadPortsForCountry(countryId);
      }
      
      // Update button class
      await nextTick();
      const button = document.querySelector(`[aria-controls="collapse-${countryId}"]`);
      const collapseElement = document.getElementById(`collapse-${countryId}`);
      
      if (button && collapseElement) {
        if (expandedCountries.value[countryId]) {
          button.classList.remove('collapsed');
          collapseElement.classList.add('show');
        } else {
          button.classList.add('collapsed');
          collapseElement.classList.remove('show');
        }
        button.setAttribute('aria-expanded', expandedCountries.value[countryId] ? 'true' : 'false');
      }
    };
    
    const resetModalState = () => {
      searchQuery.value = '';
      countryPorts.value = {};
      loadingCountryPorts.value = {};
      countryPortsLoaded.value = {};
      expandedCountries.value = {};
    };
    
    const preSelectExistingPorts = () => {
      const currentPortIds = ports.value.map(port => {
        return String(port.portID || port.id || port.portId);
      }).filter(Boolean);
      selectedPortIds.value = [...currentPortIds];
    };
    
    const getCurrentPortCountries = () => {
      const currentPortCountries = new Map();
      ports.value.forEach(port => {
        if (port.countryName) {
          const country = countryOptions.value.find(c => c.name === port.countryName);
          if (country) {
            currentPortCountries.set(country.id, country.name);
          }
        }
      });
      return currentPortCountries;
    };
    
    const loadAndExpandCountries = async (currentPortCountries) => {
      if (currentPortCountries.size === 0) return;
      
      await nextTick();
      const loadPromises = [];
      for (const [countryId] of currentPortCountries) {
        loadPromises.push(loadPortsForCountry(countryId));
      }
      await Promise.all(loadPromises);
      
      await nextTick();
      for (const [countryId] of currentPortCountries) {
        expandedCountries.value[countryId] = true;
        const collapseElement = document.getElementById(`collapse-${countryId}`);
        const button = document.querySelector(`[aria-controls="collapse-${countryId}"]`);
        if (collapseElement && button) {
          collapseElement.classList.add('show');
          button.classList.remove('collapsed');
          button.setAttribute('aria-expanded', 'true');
        }
      }
    };
    
    const openPortModal = async () => {
      resetModalState();
      await loadPorts();
      preSelectExistingPorts();
      await loadCountryOptions();
      
      const currentPortCountries = getCurrentPortCountries();
      await loadAndExpandCountries(currentPortCountries);
      
      if (portSelectionModal) {
        portSelectionModal.show();
      }
    };
    
    const closeModal = () => {
      if (portSelectionModal) {
        portSelectionModal.hide();
      }
    };
    
    const savePorts = async () => {
      if (!currentUser.value?.tenantId || !selectedPortIds.value.length) {
        return;
      }
      
      isSaving.value = true;
      
      try {
        // Prepare the ports data to save from all loaded country ports
        const portsToSave = selectedPortIds.value.map(id => {
          // Find the matching port in any of the loaded country ports
          let portOption = null;
          for (const countryId in countryPorts.value) {
            portOption = countryPorts.value[countryId].find(p => String(p.id) === String(id));
            if (portOption) break;
          }
          
          // Skip if port option not found
          if (!portOption) {
            console.warn(`Port option with ID ${id} not found`);
            return null;
          }
          
          return {
            id: portOption.id,
            portId: portOption.id,
            tenantId: currentUser.value.tenantId,
            portName: portOption.portName || portOption.name,
            portCode: portOption.portCode || portOption.code,
            country: portOption.country
          };
        }).filter(Boolean); // Remove any null values
        
        // Save the ports if there are any valid ones
        if (portsToSave.length > 0) {
          await portService.savePorts(currentUser.value.tenantId, portsToSave);
          
          // Reload the ports from the API to get the correct data
          await loadPorts();
          
          // Update entity counts
          await updatePortsCount();
          
          // Show success notification
          store.dispatch('notify', {
            message: t('profile.ports.saveSuccess', 'Ports saved successfully'),
            type: 'success'
          });
        } else {
          console.error('No valid ports to save');
          store.dispatch('notify', {
            message: t('profile.ports.noValidPorts', 'No valid ports to save'),
            type: 'error'
          });
        }
        
        // Close the modal
        if (portSelectionModal) {
          portSelectionModal.hide();
        }
      } catch (err) {
        console.error('Error saving ports:', err);
        store.dispatch('notify', {
          message: t('profile.ports.saveError', 'Failed to save ports'),
          type: 'error'
        });
      } finally {
        isSaving.value = false;
      }
    };
    
    onMounted(() => {
      loadPorts();
      
      // Initialize Bootstrap modal
      if (typeof bootstrap !== 'undefined') {
        const modalElement = document.getElementById('portSelectionModal');
        if (modalElement) {
          portSelectionModal = new bootstrap.Modal(modalElement);
          
        }
      }
    });
    
    return {
      ports,
      isLoading,
      error,
      deletingId,
      deletePort,
      openPortModal,
      closeModal,
      selectedPortIds,
      searchQuery,
      countryOptions,
      filteredCountryOptions,
      countryPorts,
      loadingCountryPorts,
      countryPortsLoaded,
      expandedCountries,
      isLoadingCountries,
      filterCountries,
      toggleCountryAccordion,
      getPortsByCountry,
      getFilteredPortsForCountry,
      getSelectedPortCountForCountry,
      getPreSelectedCountForCountry,
      isSaving,
      savePorts,
      // Pagination
      hasMorePages,
      loadMorePorts,
      loadingMore,
      totalItems,
      currentPage,
      t
    };
  }
};
</script>

<style scoped>
.port-item {
  transition: background-color 0.2s;
  padding: 6px;
  border-radius: 6px;
}

.port-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.port-selection-list {
  max-height: 350px;
  overflow-y: auto;
}

.port-selection-item {
  padding: 6px 12px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  transition: background-color 0.15s ease;
}

.port-selection-item:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.05);
}

.port-selection-item.selected {
  background-color: rgba(var(--bs-primary-rgb), 0.08);
  border-left: 3px solid var(--bs-primary);
  font-weight: 500;
}

.port-selection-label {
  cursor: pointer;
  margin: 0;
  display: block;
}

.accordion-item {
  border: 1px solid rgba(0, 0, 0, 0.125);
  margin-bottom: 0.5rem;
}

.accordion-button {
  font-size: 0.95rem;
  padding: 0.75rem 1rem;
}

.accordion-button:not(.collapsed) {
  background-color: rgba(var(--bs-primary-rgb), 0.05);
  color: inherit;
}

/* Make the load more button more visually appealing */
.btn-outline-primary {
  transition: all 0.3s;
}

.btn-outline-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
</style> 